import { useMemo } from 'react';
import { DemeterPageElementModel, DemeterPageType, DemeterRegion, DemeterSubRegion } from '../../../../Generated/Raven-Demeter';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import { ITreeNode } from '../../TreeView/TreeItem';
import TreeView from '../../TreeView/TreeView';
import { IPageElementModalSelection } from './PageElementModalDefinitions';

interface IPageElementRegionAndElementProps {
    selection: IPageElementModalSelection;
    selectedPageElement: DemeterPageElementModel | null | undefined;
    pageElements: DemeterPageElementModel[];
    handleRegionSelect: (selected: DemeterRegion | null | undefined) => void;
    handleSubRegionSelect: (selected: DemeterSubRegion | null | undefined, selectedName: string | null | undefined) => void;
    track: TrackingEvent;
}

const equals = (a: any, b: any) => String(a ?? '') === String(b ?? '');

const PageElementRegionAndElement: React.FC<IPageElementRegionAndElementProps> = (props) => {
    const [translations, translate] = useLanguage();

    const isMarketIndicators = props.selection.pageType === DemeterPageType.CommodityIndicators || props.selection.pageType === DemeterPageType.HedgeMonitorPro;

    const isFutures = props.selection.pageType === DemeterPageType.Futures;

    const useFlattenedRegion = props.selection.pageType === DemeterPageType.PhysicalPrices;

    const treeNodes: ITreeNode[] = useMemo(() => {
        const map = new Map<string, ITreeNode>();

        props.pageElements.forEach((element) => {
            const { region, subRegion, subRegionDisplayName } = element;

            const regionId = `${region}`;
            const regionName = region ? translations.region[region] || region : '';
            const regionValue = `${region}`;

            const subRegionId = `${region}|${subRegion}`;
            let subRegionName = '';
            if (subRegionDisplayName) {
                subRegionName = translate(subRegionDisplayName) || subRegionDisplayName;
            } else if (subRegion) {
                subRegionName = translate(subRegion) || subRegion;
            } else {
                subRegionName = subRegion ?? '';
            }
            const subRegionValue = `${region}|${subRegion}|${subRegionName}`;

            // Region node
            if (regionId && !map.has(regionId)) {
                map.set(regionId, {
                    id: regionId,
                    name: regionName,
                    value: regionValue,
                    parentId: '',
                    parentValue: '',
                    column: 'left',
                    selectedProduct: equals(region, props.selection.region),
                    expanded: equals(region, props.selection.region),
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }

            let regionNode = regionId ? map.get(regionId)! : null;

            // SubRegion node
            if (subRegion) {
                let subRegionNode = regionNode?.children.find((n) => n.id === subRegionId);
                if (!subRegionNode) {
                    subRegionNode = {
                        id: subRegionId,
                        name: subRegionName,
                        value: subRegionValue,
                        parentId: regionId,
                        parentValue: regionValue,
                        column: 'left',
                        selectedProduct: equals(subRegion, props.selection.subRegion),
                        expanded: false,
                        order: 0,
                        displayOrder: 0,
                        children: [],
                    };
                    regionNode?.children.push(subRegionNode);
                }

                regionNode = subRegionNode;
            }
        });

        return Array.from(map.values());
    }, [props.pageElements, props.selectedPageElement, props.selection.region, props.selection.subRegion]);

    const flattenedTreeNodes: ITreeNode[] = useMemo(() => {
        const map = new Map<string, ITreeNode>();

        props.pageElements.forEach((element) => {
            const { region, subRegion, subRegionDisplayName } = element;

            const regionName = region ? translations.region[region] || region : '';
            let subRegionName = '';
            if (subRegionDisplayName) {
                subRegionName = translate(subRegionDisplayName) || subRegionDisplayName;
            } else if (subRegion) {
                subRegionName = translate(subRegion) || subRegion;
            } else {
                subRegionName = '';
            }

            const regionId = `${region}|${subRegion}|${subRegionName}`;
            const regionValue = `${regionId}`;

            // Region node
            if (regionId && !map.has(regionId)) {
                map.set(regionId, {
                    id: regionId,
                    name: regionName,
                    value: regionValue,
                    parentId: '',
                    parentValue: '',
                    column: 'left',
                    selectedProduct: equals(region, props.selection.region),
                    expanded: equals(region, props.selection.region),
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }
        });

        return Array.from(map.values());
    }, [props.pageElements, props.selectedPageElement, props.selection.region, props.selection.subRegion]);

    const findNodeById = (nodes: ITreeNode[], id: string): ITreeNode | undefined =>
        nodes.reduce<ITreeNode | undefined>((found, node) => {
            if (found) {
                return found;
            }
            if (node.id === id) {
                return node;
            }
            return findNodeById(node.children, id);
        }, undefined);

    const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
        const { id } = event.currentTarget;
        const node = useFlattenedRegion ? findNodeById(flattenedTreeNodes, id) : findNodeById(treeNodes, id);
        if (!node) {
            return;
        }

        const [region, subRegion, subRegionDisplayName] = node.value.split('|');
        props.handleRegionSelect(region as DemeterRegion);
        if (region === 'null') {
            props.handleRegionSelect(undefined);
        } else {
            props.handleRegionSelect(region as DemeterRegion);
        }
        if (subRegion === 'null') {
            props.handleSubRegionSelect(undefined, undefined);
        } else {
            props.handleSubRegionSelect(subRegion as DemeterSubRegion, subRegionDisplayName);
        }
    };

    let treeData: ITreeNode[] = [];
    if (isMarketIndicators || isFutures) {
        treeData = [];
    } else if (useFlattenedRegion) {
        treeData = flattenedTreeNodes;
    } else {
        treeData = treeNodes;
    }

    return <TreeView data={treeData} searchTerm="" handleExpand={handleSelect} handleSelect={handleSelect} disabledNodes={[]} track={props.track} />;
};

export default PageElementRegionAndElement;
